import React, { useState } from 'react';
import { motion } from 'framer-motion';
import './Contact.css';

interface FormData {
  name: string;
  email: string;
  company: string;
  service: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('service', formData.service);
      formDataToSend.append('message', formData.message);

      const response = await fetch('/api/contact.php', {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        setSubmitMessage('¡Mensaje enviado correctamente! Te contactaremos pronto.');
        setFormData({
          name: '',
          email: '',
          company: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitMessage(result.message || 'Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
      }
    } catch (error) {
      setSubmitMessage('Error al enviar el mensaje. Por favor, inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    initial: {},
    whileInView: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section id="contact" className="contact section">
      <div className="container">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <motion.h2 
            className="section-title neon-text animate-glow"
            variants={fadeInUp}
          >
            Hablemos de tu Proyecto
          </motion.h2>

          <motion.p 
            className="contact-subtitle"
            variants={fadeInUp}
          >
            ¿Tienes una idea innovadora? ¿Necesitas fortalecer la seguridad de tu empresa? 
            Estamos aquí para convertir tus desafíos tecnológicos en soluciones exitosas.
          </motion.p>

          <div className="contact-content">
            <motion.div 
              className="contact-form-container"
              variants={fadeInUp}
            >
              <form onSubmit={handleSubmit} className="contact-form glass-card">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Nombre *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">Empresa</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nombre de tu empresa"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="service">Servicio de Interés</label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="mobile">Desarrollo Móvil</option>
                      <option value="web">Desarrollo Web</option>
                      <option value="security">Auditoría de Seguridad</option>
                      <option value="ai">Soluciones de IA/ML</option>
                      <option value="privacy">Consultoría en Privacidad</option>
                      <option value="optimization">Optimización</option>
                      <option value="custom">Proyecto Personalizado</option>
                    </select>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label htmlFor="message">Mensaje *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Cuéntanos sobre tu proyecto, necesidades y objetivos..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                </button>

                {submitMessage && (
                  <div className={`submit-message ${submitMessage.includes('Error') ? 'error' : 'success'}`}>
                    {submitMessage}
                  </div>
                )}
              </form>
            </motion.div>

            <motion.div 
              className="contact-info"
              variants={staggerContainer}
            >
              <motion.div className="info-card glass-card" variants={fadeInUp}>
                <div className="info-icon">📧</div>
                <h3>Email</h3>
                <p>r00tedbrain@immoactivatiassociatssl.com</p>
                <p>bwtsafedevstests@proton.me</p>
                <p className="info-note">Respuesta en menos de 24h</p>
              </motion.div>

              <motion.div className="info-card glass-card" variants={fadeInUp}>
                <div className="info-icon">💬</div>
                <h3>WhatsApp</h3>
                <p>Contacto directo e inmediato</p>
                <a 
                  href="https://wa.me/18255570174"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary info-btn"
                >
                  Abrir WhatsApp
                </a>
              </motion.div>

              <motion.div className="info-card glass-card" variants={fadeInUp}>
                <div className="info-icon">💼</div>
                <h3>Fiverr</h3>
                <p>Proyectos rápidos y específicos</p>
                <a 
                  href="https://www.fiverr.com/sirhoff/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary info-btn"
                >
                  Ver Perfil
                </a>
              </motion.div>

              <motion.div className="info-card glass-card" variants={fadeInUp}>
                <div className="info-icon">📺</div>
                <h3>YouTube</h3>
                <p>Tutoriales y contenido técnico</p>
                <a 
                  href="https://www.youtube.com/@greyhatspain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary info-btn"
                >
                  Suscribirse
                </a>
              </motion.div>

              <motion.div className="consultation-banner glass-card" variants={fadeInUp}>
                <h3 className="gradient-text">Consulta Gratuita</h3>
                <p>
                  Ofrecemos una consulta inicial de 30 minutos completamente gratuita 
                  para entender tus necesidades y proponerte la mejor solución.
                </p>
                <div className="consultation-benefits">
                  <div className="benefit">✓ Análisis de necesidades</div>
                  <div className="benefit">✓ Propuesta técnica</div>
                  <div className="benefit">✓ Presupuesto orientativo</div>
                  <div className="benefit">✓ Sin compromisos</div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          <motion.div 
            className="contact-footer"
            variants={fadeInUp}
          >
            <div className="privacy-note">
              <span className="privacy-icon">🔒</span>
              <p>
                Tus datos están protegidos. Cumplimos estrictamente con el GDPR y nunca 
                compartimos tu información con terceros.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Contact;